/**
 * Szybka kompresja zdjęć w przeglądarce.
 *
 * Optymalizacje vs poprzednia wersja:
 *  - `createImageBitmap` — dekoduje obraz na osobnym wątku (browser-native)
 *  - `OffscreenCanvas` — kompresja poza main thread (gdy dostępne)
 *  - `toBlob`/`convertToBlob` zamiast `toDataURL` — pomija drogą enkodację base64
 *  - `URL.createObjectURL` zamiast dataURL do podglądu — natychmiastowy
 *
 * Zwraca: { blob, previewUrl, width, height }
 *   - blob: do uploadu (multipart)
 *   - previewUrl: do <img src=...> — pamięć trzymana przez Blob, zwolnij gdy nie potrzebujesz
 *
 * Stary helper `compressImageFile` zachowany jako wrapper (zwraca dataURL).
 */
export async function compressImageFast(file, { maxWidth = 1024, quality = 0.65 } = {}) {
  // 1) Dekodowanie — preferujemy createImageBitmap (offload do worker thread browsera)
  let img;
  let usingBitmap = false;
  try {
    img = await createImageBitmap(file);
    usingBitmap = true;
  } catch (_) {
    // fallback dla starszych przeglądarek
    img = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Nie udało się odczytać pliku'));
      reader.onload = (e) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error('Nie udało się otworzyć obrazu'));
        i.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  }

  // 2) Skalowanie
  const ratio = img.width > maxWidth ? maxWidth / img.width : 1;
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);

  // 3) Canvas — OffscreenCanvas gdy dostępny (Chrome, Edge, mobile Safari 16.4+)
  let canvas;
  let useOffscreen = false;
  try {
    if (typeof OffscreenCanvas !== 'undefined') {
      canvas = new OffscreenCanvas(w, h);
      useOffscreen = true;
    } else {
      canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
    }
  } catch (_) {
    canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);

  // 4) Zwolnij pamięć ImageBitmap
  if (usingBitmap && img.close) {
    try { img.close(); } catch (_) { /* ignore */ }
  }

  // 5) Encode JPEG → Blob (znacznie szybsze niż toDataURL)
  const blob = await (useOffscreen && canvas.convertToBlob
    ? canvas.convertToBlob({ type: 'image/jpeg', quality })
    : new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality)));

  // 6) Instant preview URL — natychmiastowy, nie wymaga base64 stringa w pamięci
  const previewUrl = URL.createObjectURL(blob);

  return { blob, previewUrl, width: w, height: h };
}


/**
 * Stary helper — zostawiony dla kompatybilności (zwraca data URL JPEG).
 * Nowy kod powinien korzystać z `compressImageFast`.
 */
export async function compressImageFile(file, { maxWidth = 1280, quality = 0.7, targetKB = 350 } = {}) {
  const { blob } = await compressImageFast(file, { maxWidth, quality });
  // Konwersja blob → dataURL (tylko gdy ktoś naprawdę tego potrzebuje)
  void targetKB; // legacy param — w nowym kompresorze nie używamy iteracji jakości
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('Konwersja na dataURL nie powiodła się'));
    r.readAsDataURL(blob);
  });
}
